import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'helpers/api';
import moment from "moment-timezone";
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BasicECharts from 'components/common/BasicEChart';
import DatePicker from 'react-datepicker';
import { LineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  CanvasRenderer
]);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

const formatReadingValue = vital => {
  if (!vital || vital.reading_id === null) return '-';
  if (vital.data_type === 'text') return vital.text_value || vital.value_1 || '-';
  if (vital.data_type === 'dual') {
    return [vital.value_1, vital.value_2].filter(Boolean).join(' / ') || '-';
  }
  return vital.value_1 ?? '-';
};

const parseFilterDate = value => {
  if (!value) return null;

  const parsed = moment(value, ['MM/DD/YY', 'MM/DD/YYYY'], true);
  return parsed.isValid() ? parsed : null;
};

const formatApiDate = value => {
  const parsed = parseFilterDate(value);
  return parsed ? parsed.format('MM/DD/YYYY') : '';
};

const getDatePickerValue = value => {
  const parsed = parseFilterDate(value);
  return parsed ? parsed.toDate() : null;
};

const hasValue = value => value !== null && value !== undefined && value !== '';

const getRangeText = (row, vital) => {
  const valueOneMin = row.threshold_min ?? row.min_value ?? vital?.min_value ?? vital?.value1;
  const valueOneMax = row.threshold_max ?? row.max_value ?? vital?.max_value?? vital?.value2;
  const valueTwoMin =
    row.threshold_min_2 ?? row.min_value_2 ?? vital?.min_value_2;
  const valueTwoMax =
    row.threshold_max_2 ?? row.max_value_2 ?? vital?.max_value_2;
  const primaryRange =
    hasValue(valueOneMin) || hasValue(valueOneMax)
      ? `${hasValue(valueOneMin) ? valueOneMin : '-'} - ${
          hasValue(valueOneMax) ? valueOneMax : '-'
        }`
      : '';
  const secondaryRange =
    hasValue(valueTwoMin) || hasValue(valueTwoMax)
      ? `${hasValue(valueTwoMin) ? valueTwoMin : '-'} - ${
          hasValue(valueTwoMax) ? valueTwoMax : '-'
        }`
      : '';
  const range = [primaryRange, secondaryRange].filter(Boolean).join(' / ');

  return range ? `${range} ${row.unit || ''}`.trim() : 'Not available';
};

const PatientVitalHistoryPanel = ({ patientId, vital }) => {
  const [historyRows, setHistoryRows] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 10;
  const [filters, setFilters] = useState({
    source: '',
    from_date: '',
    to_date: ''
  });
  const [trendData, setTrendData] = useState([]);
  const [trendDays, setTrendDays] = useState(30);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);

  const vitalId = vital?.vital_sign_id || vital?.id;
  const isTextVital = vital?.data_type === 'text';

  const fetchHistoryRows = useCallback(async () => {
    if (!patientId || !vitalId) return;

    setHistoryLoading(true);
    try {
      const payload = {
        patient_id: patientId,
        vital_sign_id: vitalId,
        page: 1,
        limit: 500
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/patients/vital-readings-history`,
        payload,
        { headers: authHeaders() }
      );

      setHistoryRows(data?.data || []);
      setHistoryPage(1);
    } catch (error) {
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [patientId, vitalId]);

  const fetchTrend = useCallback(
    async days => {
      if (!patientId || !vitalId) return;

      setTrendLoading(true);
      try {
        const payload = {
          patient_id: patientId,
          vital_sign_id: vitalId,
          days
        };

        const { data } = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/patients/vital-readings-trend`,
          payload,
          { headers: authHeaders() }
        );

        setTrendData(data?.data || []);
      } catch (error) {
        setTrendData([]);
      } finally {
        setTrendLoading(false);
      }
    },
    [patientId, vitalId]
  );

  useEffect(() => {
    if (!patientId || !vitalId) return;
    setTrendDays(30);
    fetchHistoryRows();
    fetchTrend(30);
  }, [fetchHistoryRows, fetchTrend, patientId, vitalId]);

  const applyFilters = async () => {
    setHistoryPage(1);
    await fetchTrend(trendDays);
  };

  const updateTrendDays = async days => {
    setTrendDays(days);
    setHistoryPage(1);
    await fetchTrend(days);
  };

  const chartVitals = useMemo(
    () => trendData.filter(item => item.data_type !== 'text'),
    [trendData]
  );

  const filteredHistoryRows = useMemo(
    () =>
      historyRows.filter(row => {
        if (filters.source && row.source !== filters.source) return false;

        if (trendDays) {
          const readingDate = row.reading_datetime
            ? moment(row.reading_datetime)
            : null;
          if (!readingDate?.isValid()) return false;

          const cutoffDate = moment().subtract(trendDays, 'days').startOf('day');
          if (readingDate.isBefore(cutoffDate)) {
            return false;
          }
        }

        return true;
      }),
    [filters.source, historyRows, trendDays]
  );

  const historyTotalPages = Math.max(
    1,
    Math.ceil(filteredHistoryRows.length / historyLimit)
  );

  const pagedHistoryRows = useMemo(() => {
    const start = (historyPage - 1) * historyLimit;
    return filteredHistoryRows.slice(start, start + historyLimit);
  }, [filteredHistoryRows, historyPage]);

  const getTrendOptions = () => {
    const getSeriesStyle = readings => {
      const isSingleReading = (readings || []).length === 1;

      return {
        type: 'line',
        smooth: true,
        showSymbol: isSingleReading,
        showAllSymbol: isSingleReading,
        symbol: 'circle',
        symbolSize: isSingleReading ? 9 : 7,
        lineStyle: { width: 3 },
        emphasis: { focus: 'series' },
        areaStyle: { opacity: 0.08 }
      };
    };

    const dateLabels = [
      ...new Set(
        chartVitals.flatMap(item =>
          (item.readings || []).map(reading =>
            moment(reading.reading_datetime).format('MM/DD')
          )
        )
      )
    ];

    const series = chartVitals.flatMap(item => {
      const readings = item.readings || [];
      const baseSeries = getSeriesStyle(readings);

      if (item.data_type === 'dual') {
        return [
          {
            ...baseSeries,
            name: `${item.short_name || item.name} systolic`,
            color: '#2563eb',
            data: readings.map(reading => [
              moment(reading.reading_datetime).format('MM/DD'),
              Number(reading.value_1)
            ])
          },
          {
            ...baseSeries,
            name: `${item.short_name || item.name} diastolic`,
            color: '#14b8a6',
            areaStyle: { opacity: 0.04 },
            data: readings.map(reading => [
              moment(reading.reading_datetime).format('MM/DD'),
              Number(reading.value_2)
            ])
          }
        ];
      }

      return [
        {
          ...baseSeries,
          name: item.short_name || item.name,
          color: '#2563eb',
          data: readings.map(reading => [
            moment(reading.reading_datetime).format('MM/DD'),
            Number(reading.value_1)
          ])
        }
      ];
    });

    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: value => (Number.isFinite(value) ? value : '-')
      },
      legend: {
        bottom: 0,
        icon: 'roundRect',
        itemWidth: 14,
        itemHeight: 8,
        textStyle: { color: '#64748b', fontSize: 11 }
      },
      grid: {
        top: 18,
        left: 36,
        right: 18,
        bottom: 54
      },
      xAxis: {
        type: 'category',
        show: true,
        boundaryGap: false,
        data: dateLabels,
        axisLine: { lineStyle: { color: '#d8e2ef' } },
        axisLabel: { color: '#64748b' }
      },
      yAxis: {
        type: 'value',
        show: true,
        splitLine: { lineStyle: { color: '#edf2f9' } },
        axisLabel: { color: '#64748b' }
      },
      series
    };
  };

  return (
    <div className="patient-vitals-v2">
      <Card className="vitals-panel">
        <Card.Header className="vitals-panel-header">
          <div className="d-flex align-items-center gap-2">
            <span className="vitals-panel-icon teal">
              <FontAwesomeIcon icon="chart-line" />
            </span>
            <div>
              <h6 className="mb-0">{vital?.name || 'Vital'} Trend</h6>
              <div className="fs-11 text-600">
                {vital?.unit ? `Unit: ${vital.unit}` : 'Per-vital trend'}
              </div>
            </div>
          </div>
          <ButtonGroup size="sm">
            {[7, 30, 90].map(days => (
              <Button
                key={days}
                variant={Number(trendDays) === days ? 'primary' : 'falcon-default'}
                onClick={() => updateTrendDays(days)}
              >
                {days}d
              </Button>
            ))}
          </ButtonGroup>
        </Card.Header>
        <Card.Body>
          <div className="vitals-chart-shell">
            {trendLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading trend...
              </div>
            ) : chartVitals.some(item => (item.readings || []).length > 0) ? (
              <BasicECharts
                echarts={echarts}
                options={getTrendOptions()}
                style={{ height: 300 }}
              />
            ) : (
              <div className="vitals-empty-state">
                <FontAwesomeIcon icon="chart-line" />
                <span>
                  {isTextVital
                    ? 'Text vitals are shown in the table'
                    : 'No trend data available'}
                </span>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="vitals-panel mt-3">
        {/* <Card.Header className="vitals-panel-header">
          <fieldset className="filters-section vitals-filter-fieldset w-100">
            <legend className="filter-heading">Filters</legend>
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Form.Label className="fs-11 mb-1">From</Form.Label>
                <DatePicker
                  placeholderText="MM/DD/YY"
                  className="form-control form-control-sm"
                  selected={getDatePickerValue(filters.from_date)}
                  onChange={date => {
                    setHistoryPage(1);
                    setFilters(prev => ({
                      ...prev,
                      from_date: date ? moment(date).format('MM/DD/YY') : ''
                    }));
                  }}
                  dateFormat="MM/dd/yy"
                />
              </Col>
              <Col md={4}>
                <Form.Label className="fs-11 mb-1">To</Form.Label>
                <DatePicker
                  placeholderText="MM/DD/YY"
                  className="form-control form-control-sm"
                  selected={getDatePickerValue(filters.to_date)}
                  onChange={date => {
                    setHistoryPage(1);
                    setFilters(prev => ({
                      ...prev,
                      to_date: date ? moment(date).format('MM/DD/YY') : ''
                    }));
                  }}
                  dateFormat="MM/dd/yy"
                />
              </Col>
              {/* <Col md={4} className="text-md-end">
                <Button
                  size="sm"
                  variant="falcon-default"
                  onClick={applyFilters}
                  disabled={historyLoading || trendLoading}
                >
                  Update Trend
                </Button>
              </Col> */}
            {/* </Row>
          </fieldset>
        </Card.Header>  */}
        <Card.Body className="p-0">
          <Table
            responsive
            hover
            striped
            size="sm"
            className="fs-10 mb-0 overflow-hidden vitals-history-table"
          >
            <thead className="bg-200 text-nowrap">
              <tr>
                <th>Reading</th>
                {/* <th>Source</th> */}
                <th>Prescription</th>
                <th>Recorded By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading history...
                  </td>
                </tr>
              ) : pagedHistoryRows.length > 0 ? (
                pagedHistoryRows.map((row, index) => (
                  <tr
                    key={`${row.vital_sign_id}-${row.reading_id}-${index}`}
                    className="text-nowrap"
                  >
                    <td>
                      {Number(row.is_out_of_range) === 1 ? (
                        // <OverlayTrigger
                        //   placement="top"
                        //   overlay={
                        //     <Tooltip id={`vital-range-${row.reading_id || index}`}>
                        //       Current out of range. Actual range:{' '}
                        //       {getRangeText(row, vital)}
                        //     </Tooltip>
                        //   }
                        // >
                        // </OverlayTrigger>
                          <span className="text-danger fw-semibold">
                            {formatReadingValue(row)} {row.unit || ''}
                          </span>
                      ) : (
                        <>
                          {formatReadingValue(row)} {row.unit || ''}
                        </>
                      )}
                    </td>
                    {/* <td>{sourceBadge(row.source)}</td> */}
                    <td>
                      {row.drug_name
                        ? `${row.drug_name} ${row.drug_strength || ''}`
                        : '-'}
                    </td>
                    <td>
                      {[row.recorded_by_first_name, row.recorded_by_last_name]
                        .filter(Boolean)
                        .join(' ') || '-'}
                    </td>
                    <td>
                      {row.reading_datetime
                        ? moment(row.reading_datetime, "YYYY-MM-DD","America/New_York").format('MM/DD/YY h:mm A')
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <div className="vitals-empty-state py-4">
                      <FontAwesomeIcon icon="list-alt" />
                      <span>No vital history available</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center">
          <span className="fs-11 text-600">
            Page {historyPage} of {historyTotalPages} -{' '}
            {filteredHistoryRows.length} records
          </span>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="falcon-default"
              disabled={historyPage <= 1 || historyLoading}
              onClick={() => setHistoryPage(page => Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="falcon-default"
              disabled={historyPage >= historyTotalPages || historyLoading}
              onClick={() =>
                setHistoryPage(page => Math.min(historyTotalPages, page + 1))
              }
            >
              Next
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
};

PatientVitalHistoryPanel.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  vital: PropTypes.object
};

PatientVitalHistoryPanel.defaultProps = {
  vital: null
};

export default PatientVitalHistoryPanel;
